<template>
  <Modal :modalActive="modalActive" @cerrar-modal="onCerrar">
    <div class="mel-wrap">

      <!-- Cabecera -->
      <div class="mel-header">
        <span class="mel-badge">Landing</span>
        <h5 class="mel-title">Editar diseño de landing</h5>
      </div>

      <!-- Estado de carga inicial -->
      <div v-if="cargando" class="mel-state">
        <i class="fa fa-spinner fa-spin"></i>
        <span>Cargando…</span>
      </div>
      <div v-else-if="errorCarga" class="mel-state mel-state--error">
        <i class="fa fa-exclamation-circle"></i>
        <span>{{ errorCarga }}</span>
      </div>

      <!-- Formulario -->
      <template v-else>

        <!-- ── Cambiar plantilla ───────────────────────────────── -->
        <div class="mel-tpl-section">
          <div class="mel-tpl-header" @click="mostrarCambioTemplate = !mostrarCambioTemplate">
            <div class="mel-tpl-current">
              <span class="mel-label">Plantilla actual</span>
              <span class="mel-tpl-nombre">{{ templateActual.nombre || '—' }}</span>
              <span v-if="templateActual.categoria" class="mel-cat" :class="'mel-cat--' + templateActual.categoria">
                {{ catLabel(templateActual.categoria) }}
              </span>
            </div>
            <button class="mel-tpl-toggle" type="button">
              <i class="fa" :class="mostrarCambioTemplate ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              Cambiar
            </button>
          </div>

          <!-- Lista de plantillas -->
          <div v-if="mostrarCambioTemplate" class="mel-tpl-list">
            <!-- Por defecto -->
            <div
              v-if="templatePorDefecto"
              class="mel-tpl-item mel-tpl-item--pdef"
              :class="{ 'mel-tpl-item--sel': form.template_id === templatePorDefecto.id && esPorDefecto }"
              @click="seleccionarPorDefecto"
            >
              <i class="fa fa-star mel-star-icon"></i>
              <span class="mel-tpl-item-nombre">Por defecto</span>
              <span class="mel-cat mel-cat--default">Estándar</span>
              <i v-if="form.template_id === templatePorDefecto.id && esPorDefecto" class="fa fa-check mel-tpl-check"></i>
            </div>
            <div class="mel-tpl-divider"></div>
            <div
              v-for="t in templates.filter(t => t.slug !== 'oscura')"
              :key="t.id"
              class="mel-tpl-item"
              :class="{ 'mel-tpl-item--sel': form.template_id === t.id && !esPorDefecto }"
              @click="seleccionarTemplate(t)"
            >
              <span class="mel-tpl-item-nombre">{{ t.nombre }}</span>
              <span class="mel-cat" :class="'mel-cat--' + t.categoria">{{ catLabel(t.categoria) }}</span>
              <i v-if="form.template_id === t.id && !esPorDefecto" class="fa fa-check mel-tpl-check"></i>
            </div>
          </div>
        </div>

        <!-- ── Campos de texto ────────────────────────────────── -->
        <div class="mel-form">

          <!-- Nombre / Título (con emoji) -->
          <div class="mel-field emoji-field" v-click-outside="() => cerrarEmoji('nombre')">
            <div class="label-emoji-row">
              <label class="mel-label">Nombre / Título</label>
              <button type="button" class="btn-emoji-toggle" @click.stop="toggleEmoji('nombre')" title="Insertar emoji">😊</button>
            </div>
            <div v-if="emojiPanelAbierto === 'nombre'" class="emoji-panel">
              <div v-for="(lista, cat) in EMOJIS" :key="cat" class="emoji-cat">
                <div class="emoji-cat-label">{{ cat }}</div>
                <div class="emoji-grid">
                  <span v-for="e in lista" :key="e" class="emoji-item" @click="insertarEmoji('nombre', e)">{{ e }}</span>
                </div>
              </div>
            </div>
            <input
              ref="refNombre"
              v-model="form.nombre"
              class="mel-input"
              type="text"
              maxlength="120"
              @blur="guardarCursor('nombre')"
              @click="guardarCursor('nombre')"
              @keyup="guardarCursor('nombre')"
            />
          </div>

          <!-- Subtítulo (con emoji) -->
          <div class="mel-field emoji-field" v-click-outside="() => cerrarEmoji('subtitulo')">
            <div class="label-emoji-row">
              <label class="mel-label">Subtítulo</label>
              <button type="button" class="btn-emoji-toggle" @click.stop="toggleEmoji('subtitulo')" title="Insertar emoji">😊</button>
            </div>
            <div v-if="emojiPanelAbierto === 'subtitulo'" class="emoji-panel">
              <div v-for="(lista, cat) in EMOJIS" :key="cat" class="emoji-cat">
                <div class="emoji-cat-label">{{ cat }}</div>
                <div class="emoji-grid">
                  <span v-for="e in lista" :key="e" class="emoji-item" @click="insertarEmoji('subtitulo', e)">{{ e }}</span>
                </div>
              </div>
            </div>
            <input
              ref="refSubtitulo"
              v-model="form.subtitulo"
              class="mel-input"
              type="text"
              maxlength="255"
              @blur="guardarCursor('subtitulo')"
              @click="guardarCursor('subtitulo')"
              @keyup="guardarCursor('subtitulo')"
            />
          </div>

          <!-- Mensaje (con emoji) -->
          <div class="mel-field emoji-field" v-click-outside="() => cerrarEmoji('mensaje')">
            <div class="label-emoji-row">
              <label class="mel-label">Mensaje promocional</label>
              <button type="button" class="btn-emoji-toggle" @click.stop="toggleEmoji('mensaje')" title="Insertar emoji">😊</button>
            </div>
            <div v-if="emojiPanelAbierto === 'mensaje'" class="emoji-panel">
              <div v-for="(lista, cat) in EMOJIS" :key="cat" class="emoji-cat">
                <div class="emoji-cat-label">{{ cat }}</div>
                <div class="emoji-grid">
                  <span v-for="e in lista" :key="e" class="emoji-item" @click="insertarEmoji('mensaje', e)">{{ e }}</span>
                </div>
              </div>
            </div>
            <textarea
              ref="refMensaje"
              v-model="form.mensaje"
              class="mel-input mel-textarea"
              maxlength="500"
              rows="3"
              @blur="guardarCursor('mensaje')"
              @click="guardarCursor('mensaje')"
              @keyup="guardarCursor('mensaje')"
            />
            <span class="mel-counter">{{ form.mensaje.length }}/500</span>
          </div>

          <!-- Idioma de los textos -->
          <div class="mel-field">
            <label class="mel-label">Idioma de los textos fijos de la landing</label>
            <select v-model="form.idioma" class="mel-input mel-select">
              <option value="es">Español</option>
              <option value="en">English</option>
              <option value="pt">Português</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
              <option value="it">Italiano</option>
            </select>
          </div>

          <!-- Banner toggle (solo si el tipster tiene banner) -->
          <div v-if="tipsterInfo && tipsterInfo.tiene_banner" class="mel-banner-toggle">
            <label class="mel-toggle-label">
              <input type="checkbox" v-model="conBanner" class="mel-toggle-input">
              <span class="mel-toggle-box"><i class="fa fa-image me-1"></i> Incluir Banner</span>
            </label>
          </div>

          <!-- CTA Link -->
          <div class="mel-field">
            <label class="mel-label">Enlace del botón (CTA)</label>
            <input
              v-model="form.cta_link"
              class="mel-input"
              type="url"
              placeholder="https://t.me/..."
              maxlength="500"
            />
          </div>

          <!-- Pixel Meta — solo publicistas (rol 5) -->
          <div class="mel-field" v-if="esPublicista">
            <label class="mel-label">Pixel de Meta Ads <span class="mel-opt">(opcional)</span></label>
            <input
              v-model="form.pixel_meta_id"
              class="mel-input"
              type="text"
              placeholder="123456789012345"
              maxlength="64"
            />
          </div>

          <!-- ID Campaña Meta (para cruzar con compras) — solo publicistas -->
          <div class="mel-field" v-if="esPublicista">
            <label class="mel-label">ID Campaña Meta Ads <span class="mel-opt">(opcional)</span></label>
            <input
              v-model="form.meta_campaign_id"
              class="mel-input"
              type="text"
              placeholder="120200012345678"
              maxlength="30"
            />
            <p class="mel-help">Vincular esta landing con una campaña Meta para ver compras reales en Publicidad Meta.</p>
          </div>

        </div>

        <!-- URL pública -->
        <div class="mel-url-row" v-if="urlPublica">
          <span class="mel-url-label">URL</span>
          <a :href="urlPublica" target="_blank" class="mel-url-link">{{ urlPublica }}</a>
          <button class="mel-copy-btn" @click="copiarUrl" :title="copiado ? 'Copiado' : 'Copiar'">
            <i class="fa" :class="copiado ? 'fa-check' : 'fa-copy'"></i>
          </button>
        </div>

        <!-- Error al guardar -->
        <p v-if="errorGuardar" class="mel-error">{{ errorGuardar }}</p>

        <!-- Acciones -->
        <div class="mel-actions">
          <button class="mel-btn mel-btn--sec" @click="onCerrar" :disabled="guardando">Cancelar</button>
          <button class="mel-btn mel-btn--pri" @click="guardar" :disabled="guardando">
            <i v-if="guardando" class="fa fa-spinner fa-spin me-1"></i>
            <i v-else class="fa fa-save me-1"></i>
            Guardar cambios
          </button>
        </div>
      </template>

    </div>
  </Modal>
</template>

<script>
import Modal from './Modal.vue';
import campanasService from '@/services/campanas.service.js';

const EMOJIS = {
  'Deportes': ['⚽','🏀','🏈','⚾','🎾','🏐','🎱','🏆','🥇','🏅','🎯','🤾','🏋️','🥊','🤺'],
  'Éxito':    ['✅','💯','🔥','⭐','🌟','✨','💎','📈','💰','🤑','💪','🎉','🎊','🚀','👑'],
  'Info':     ['📢','📣','🔔','⚠️','❗','❓','➡️','👉','👆','📌','🗓️','📊','🏷️','🔑','🛡️'],
  'Caras':    ['😊','😎','🤩','🙌','👏','🥳','😍','🤔','🫡','💪','🫶','🤝','👀','🫰','✌️'],
};

const CAT_LABELS = {
  'clasica':      'Clásica',
  'foto':         'Foto',
  'minimal':      'Minimal',
  'especial':     'Especial',
  'especial-foto':'Esp. foto',
};

export default {
  name: 'ModalEditarLanding',
  components: { Modal },

  directives: {
    clickOutside: {
      mounted(el, binding) {
        el._clickOutsideHandler = (e) => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener('click', el._clickOutsideHandler);
      },
      unmounted(el) {
        document.removeEventListener('click', el._clickOutsideHandler);
      },
    },
  },

  props: {
    modalActive: { type: Boolean, default: false },
    landingId:   { type: Number, default: 0 },
  },

  emits: ['cerrar-modal', 'guardado'],

  data() {
    return {
      EMOJIS,
      cargando: false,
      errorCarga: '',
      guardando: false,
      errorGuardar: '',
      copiado: false,
      urlPublica: '',
      mostrarCambioTemplate: false,
      esPorDefecto: false,
      emojiPanelAbierto: null,
      cursores: { nombre: 0, subtitulo: 0, mensaje: 0 },
      templates: [],
      templateActual: { id: null, nombre: '', categoria: '' },
      tipsterInfo: null,
      conBanner: false,
      form: {
        template_id:      null,
        nombre:           '',
        subtitulo:        '',
        mensaje:          '',
        idioma:           'es',
        cta_link:         '',
        pixel_meta_id:    '',
        meta_campaign_id: '',
      },
    };
  },

  computed: {
    esPublicista() {
      return Number(this.$store.getters['auth/rolEfectivo']) === 5;
    },
    templatePorDefecto() {
      return this.templates.find(t => t.slug === 'oscura') || this.templates[0] || null;
    },
  },

  watch: {
    modalActive(val) {
      if (val && this.landingId) this.cargar();
    },
  },

  methods: {
    async cargar() {
      this.cargando = true;
      this.errorCarga = '';
      this.errorGuardar = '';
      this.copiado = false;
      this.mostrarCambioTemplate = false;
      this.emojiPanelAbierto = null;
      try {
        const [detalle, catalogo] = await Promise.all([
          campanasService.landingDetalle(this.landingId),
          campanasService.landingCatalogo(),
        ]);

        this.templates = catalogo.templates || [];

        const landing = detalle.landing;
        const defaults = detalle.defaults;
        const ov = landing.overrides || {};
        const tpl = landing.template || {};

        this.templateActual = { id: tpl.id, nombre: tpl.nombre, categoria: tpl.categoria };
        this.tipsterInfo = detalle.tipster_info || null;
        this.conBanner = detalle.con_banner || false;
        this.urlPublica = landing.url_publica || (process.env.VUE_APP_API_BASE_URL || '') + '/l/' + landing.slug_publico;

        const idiomaPersonalizacion = landing.tipster_landing?.idioma || 'es';
        this.form = {
          template_id:   tpl.id || null,
          nombre:        ov['{{NOMBRE}}']    || defaults.nombre    || '',
          subtitulo:     ov['{{SUBTITULO}}'] || defaults.subtitulo || '',
          mensaje:       ov['{{MENSAJE}}']   || defaults.mensaje   || '',
          idioma:        ov['idioma_override'] || idiomaPersonalizacion,
          cta_link:         landing.cta_link         || '',
          pixel_meta_id:    landing.pixel_meta_id    || '',
          meta_campaign_id: landing.meta_campaign_id || '',
        };
      } catch {
        this.errorCarga = 'No se pudo cargar la landing.';
      } finally {
        this.cargando = false;
      }
    },

    seleccionarPorDefecto() {
      if (!this.templatePorDefecto) return;
      this.esPorDefecto = true;
      this.form.template_id = this.templatePorDefecto.id;
      this.templateActual = { id: this.templatePorDefecto.id, nombre: 'Por defecto', categoria: this.templatePorDefecto.categoria };
      this.mostrarCambioTemplate = false;
    },

    seleccionarTemplate(t) {
      this.esPorDefecto = false;
      this.form.template_id = t.id;
      this.templateActual = { id: t.id, nombre: t.nombre, categoria: t.categoria };
      this.mostrarCambioTemplate = false;
    },

    catLabel(cat) {
      return CAT_LABELS[cat] || cat;
    },

    toggleEmoji(campo) {
      this.emojiPanelAbierto = this.emojiPanelAbierto === campo ? null : campo;
    },
    cerrarEmoji(campo) {
      if (this.emojiPanelAbierto === campo) this.emojiPanelAbierto = null;
    },
    guardarCursor(campo) {
      const refs = { nombre: this.$refs.refNombre, subtitulo: this.$refs.refSubtitulo, mensaje: this.$refs.refMensaje };
      const el = refs[campo];
      if (el) this.cursores[campo] = el.selectionStart ?? (this.form[campo] || '').length;
    },
    insertarEmoji(campo, emoji) {
      const refs = { nombre: this.$refs.refNombre, subtitulo: this.$refs.refSubtitulo, mensaje: this.$refs.refMensaje };
      const el = refs[campo];
      const pos = el ? (el.selectionStart ?? this.cursores[campo]) : this.cursores[campo];
      const actual = this.form[campo] || '';
      this.form[campo] = actual.slice(0, pos) + emoji + actual.slice(pos);
      const nuevaPos = pos + [...emoji].length;
      this.cursores[campo] = nuevaPos;
      this.$nextTick(() => {
        if (el) { el.selectionStart = el.selectionEnd = nuevaPos; el.focus(); }
      });
    },

    async guardar() {
      this.guardando = true;
      this.errorGuardar = '';
      try {
        const payload = { ...this.form };
        if (this.tipsterInfo && this.tipsterInfo.tiene_banner) {
          payload.con_banner = this.conBanner;
        }
        await campanasService.landingEditar(this.landingId, payload);
        this.$emit('guardado');
        this.onCerrar();
      } catch (err) {
        this.errorGuardar = err?.response?.data?.message || 'Error al guardar.';
      } finally {
        this.guardando = false;
      }
    },

    async copiarUrl() {
      try {
        await navigator.clipboard.writeText(this.urlPublica);
        this.copiado = true;
        setTimeout(() => { this.copiado = false; }, 2000);
      } catch { /* noop */ }
    },

    onCerrar() {
      this.$emit('cerrar-modal');
    },
  },
};
</script>

<style scoped>
.mel-wrap {
  padding: 8px 4px 4px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Cabecera */
.mel-header { display: flex; align-items: center; gap: 10px; }
.mel-badge {
  font-size: 10px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
  color: var(--q-gold); border: 1px solid var(--q-gold); border-radius: 4px; padding: 1px 6px; flex-shrink: 0;
}
.mel-title { font-family: var(--q-font-display); color: var(--q-text); font-size: 1rem; margin: 0; }

/* ── Sección cambiar plantilla ───────────── */
.mel-tpl-section {
  border: 1px solid var(--q-border-md);
  border-radius: 8px;
  overflow: hidden;
}
.mel-tpl-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: var(--q-card-alt);
  cursor: pointer;
  gap: 12px;
}
.mel-tpl-header:hover { background: var(--q-hover); }
.mel-tpl-current {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}
.mel-tpl-nombre {
  font-size: .85rem;
  font-weight: 600;
  color: var(--q-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mel-tpl-toggle {
  display: flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: 1px solid var(--q-border-md);
  border-radius: 5px;
  color: var(--q-muted);
  font-size: .75rem;
  font-weight: 600;
  padding: 4px 10px;
  cursor: pointer;
  white-space: nowrap;
  transition: color .15s, border-color .15s;
}
.mel-tpl-toggle:hover { color: var(--q-gold); border-color: var(--q-gold); }

/* Lista de plantillas */
.mel-tpl-list {
  max-height: 200px;
  overflow-y: auto;
  border-top: 1px solid var(--q-border);
  padding: 4px 0;
}
.mel-tpl-list::-webkit-scrollbar { width: 3px; }
.mel-tpl-list::-webkit-scrollbar-thumb { background: var(--q-border-md); border-radius: 3px; }

.mel-tpl-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  cursor: pointer;
  transition: background .1s;
}
.mel-tpl-item:hover { background: var(--q-hover); }
.mel-tpl-item--sel { background: color-mix(in srgb, var(--q-gold) 8%, var(--q-card)); }
.mel-tpl-item-nombre { flex: 1; font-size: .82rem; color: var(--q-text); }
.mel-tpl-check { color: var(--q-gold); font-size: .8rem; }

/* Categorías */
.mel-cat {
  font-size: .62rem; font-weight: 600; text-transform: uppercase; letter-spacing: .04em;
  border-radius: 3px; padding: 1px 5px; flex-shrink: 0;
  background: var(--q-border); color: var(--q-muted);
}
.mel-cat--clasica      { background: #0c2a1a; color: var(--q-green); }
.mel-cat--foto         { background: #1a1a0c; color: var(--q-gold); }
.mel-cat--minimal      { background: #0a1828; color: #60b0ff; }
.mel-cat--especial     { background: #2c1216; color: var(--q-red); }
.mel-cat--especial-foto{ background: #1a0a2e; color: #c084fc; }
.mel-cat--default      { background: color-mix(in srgb, var(--q-gold) 15%, transparent); color: var(--q-gold); }

.mel-tpl-item--pdef { background: color-mix(in srgb, var(--q-gold) 4%, var(--q-card)); }
.mel-tpl-item--pdef:hover { background: color-mix(in srgb, var(--q-gold) 8%, var(--q-card)); }
.mel-star-icon { color: var(--q-gold); font-size: .72rem; flex-shrink: 0; }
.mel-tpl-divider { height: 1px; background: var(--q-border); margin: 2px 12px; }

/* ── Formulario ──────────────────────────── */
.mel-form { display: flex; flex-direction: column; gap: 12px; }

.mel-field {
  display: flex; flex-direction: column; gap: 4px; position: relative;
}

.mel-label {
  font-size: .72rem; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: var(--q-muted);
}
.mel-opt  { font-weight: 400; text-transform: none; letter-spacing: 0; }
.mel-help { font-size: .72rem; color: var(--q-muted); margin: 3px 0 0; }

.mel-input {
  background: var(--q-input-bg, var(--q-card-alt));
  border: 1px solid var(--q-border-md);
  border-radius: 6px;
  color: var(--q-text);
  font-size: .85rem;
  padding: 8px 10px;
  outline: none;
  width: 100%;
  transition: border-color .15s;
  font-family: inherit;
}
.mel-input:focus { border-color: var(--q-gold); }
.mel-input::placeholder { color: var(--q-muted); opacity: .6; }
.mel-textarea { resize: vertical; min-height: 72px; }
.mel-select { cursor: pointer; appearance: auto; }
.mel-counter { font-size: .68rem; color: var(--q-muted); text-align: right; margin-top: 2px; }

/* ── Emoji picker ────────────────────────── */
.emoji-field { position: relative; }

.label-emoji-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.btn-emoji-toggle {
  background: transparent;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
  opacity: .7;
  transition: opacity .15s;
}
.btn-emoji-toggle:hover { opacity: 1; }

.emoji-panel {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 200;
  background: var(--q-card);
  border: 1px solid var(--q-border-md);
  border-radius: 8px;
  padding: 10px;
  width: 280px;
  box-shadow: 0 8px 24px rgba(0,0,0,.4);
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}
.emoji-cat { display: flex; flex-direction: column; gap: 4px; }
.emoji-cat-label {
  font-size: .65rem; font-weight: 700; text-transform: uppercase; letter-spacing: .06em;
  color: var(--q-muted);
}
.emoji-grid { display: flex; flex-wrap: wrap; gap: 2px; }
.emoji-item {
  font-size: 1.2rem; cursor: pointer; border-radius: 4px; padding: 3px;
  transition: background .1s;
}
.emoji-item:hover { background: var(--q-hover); }

/* ── URL row ─────────────────────────────── */
.mel-url-row {
  display: flex; align-items: center; gap: 8px;
  background: var(--q-card-alt); border: 1px solid var(--q-border); border-radius: 6px; padding: 7px 10px;
}
.mel-url-label {
  font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .06em;
  color: var(--q-muted); flex-shrink: 0;
}
.mel-url-link {
  flex: 1; font-family: var(--q-font-mono); font-size: .74rem; color: var(--q-green);
  word-break: break-all; text-decoration: none;
}
.mel-url-link:hover { text-decoration: underline; }
.mel-copy-btn {
  background: transparent; border: 1px solid var(--q-border-md); border-radius: 4px;
  color: var(--q-muted); padding: 3px 7px; cursor: pointer; flex-shrink: 0;
  transition: color .15s; font-size: .8rem;
}
.mel-copy-btn:hover { color: var(--q-green); border-color: var(--q-green); }

/* ── Error ───────────────────────────────── */
.mel-error { font-size: .8rem; color: var(--q-red); margin: 0; }

/* ── Acciones ────────────────────────────── */
.mel-actions {
  display: flex; justify-content: flex-end; gap: 8px;
  padding-top: 8px; border-top: 1px solid var(--q-border);
}
.mel-btn {
  border: none; border-radius: 6px; font-size: .83rem; font-weight: 600; padding: 7px 18px;
  cursor: pointer; transition: opacity .15s;
}
.mel-btn:disabled { opacity: .4; cursor: not-allowed; }
.mel-btn--pri { background: var(--q-gold); color: var(--q-gold-on); }
.mel-btn--pri:hover:not(:disabled) { background: var(--q-gold-hover); }
.mel-btn--sec { background: transparent; border: 1px solid var(--q-border-md); color: var(--q-muted); }
.mel-btn--sec:hover:not(:disabled) { border-color: var(--q-text); color: var(--q-text); }

/* ── Estado genérico ─────────────────────── */
.mel-state {
  display: flex; align-items: center; gap: 10px; padding: 24px 0;
  color: var(--q-muted); font-size: .9rem; justify-content: center;
}
.mel-state--error { color: var(--q-red); }

/* ── Banner toggle ───────────────────────── */
.mel-banner-toggle {
  display: flex;
  align-items: center;
}
.mel-toggle-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}
.mel-toggle-input {
  width: 15px;
  height: 15px;
  accent-color: var(--q-gold);
  cursor: pointer;
  flex-shrink: 0;
}
.mel-toggle-box {
  font-size: .8rem;
  font-weight: 600;
  color: var(--q-text);
}

/* ── Responsive ──────────────────────────── */
@media (max-width: 500px) {
  .mel-actions { flex-direction: column; }
  .mel-btn { width: 100%; text-align: center; }
  .emoji-panel { width: 240px; }
}
</style>
